import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { authClient } from '../utils/amplifyClient';
import { useUserContext } from '../contexts/UserContext';
import {
    listArchivedDiscrepancyByCompanyIdAndUpdatedAt,
    listDiscrepancyByCompanyIdAndUpdatedAt as listDiscrepancyByCompanyIdAndUpdatedAtQuery,
    listArchivedDiscrepancyByEquipmentIdAndUpdatedAt,
    getReport as getReportQuery,
} from '../graphql/queries';

import {
    updateDiscrepancy as updateDiscrepancyMutation,
    updateArchivedDiscrepancy,
    updateReport as updateReportMutation,
    updateCompanyOwner,
    updateUser,
} from '../graphql/mutations';

import { ArchivedDiscrepancy, Discrepancy, ModelSortDirection, Location } from '../types/schema';
import { tDiscrepancyLog, tGroupedEquipment, tSelectLabelValue } from '../types';
import {
    ALL_EQUIPMENT,
    ITEM_STATUS_CODES,
    ITEM_STATUS_CONTENT,
    LOCAL_STORAGE_KEYS,
    KEYS_START_OF_DAY_REPORT,
    SEARCH_PARAMS,
} from '../utils/constants';

import { hasPermission, Permission } from '../utils/permissions';
import { archiveDiscrepancyRecord } from '../utils/issueArchiveUtils';
import {
    ISSUES_TAB_QUERY,
    parseIssuesListTab,
    buildIssueEquipmentDetailPath,
    filterIssuesByUnitName,
} from '../utils/issueUtils';
import { useEquipmentContext } from '../contexts/EquipmentContext';
import { useRegionAndLocationContext } from '../contexts/RegionAndLocationContext';
import { useAppContext } from '../contexts/AppContext';
import '../css/IssuesContent.css';
import {
    Card,
    Btn,
    BtnIconOnly,
    IconAlertTriangle,
    PageHeader,
    TabNav,
    DataTable,
    IconListSquare,
    IconPencil,
    IconClipboard,
    Pagination,
} from '@ui';
import type { DataTableColumn, SortDirection } from '@ui';

const ARCHIVED_DEFAULT_SORT_COLUMN_ID = 'archivedDate';
const ISSUES_TABLE_COLUMN_COUNT = 5;

const formatIssueDate = (value?: string | null) => {
    if (!value) {
        return '--';
    }
    try {
        return new Date(value).toLocaleDateString();
    } catch {
        return '--';
    }
};

const getIssueDateSortValue = (value?: string | null) => (value ? new Date(value).getTime() : 0);

const compareArchivedIssueRows = (
    a: ArchivedDiscrepancy,
    b: ArchivedDiscrepancy,
    sortValue: (row: ArchivedDiscrepancy) => string | number,
    sortType: 'alpha' | 'numeric',
    direction: SortDirection,
) => {
    const aVal = sortValue(a);
    const bVal = sortValue(b);
    let result = 0;

    if (sortType === 'numeric') {
        const aNum = typeof aVal === 'number' ? aVal : Number(aVal);
        const bNum = typeof bVal === 'number' ? bVal : Number(bVal);
        result = aNum - bNum;
    } else {
        result = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
    }

    return direction === 'desc' ? -result : result;
};

const CURRENT_ISSUE_STATUS_SORT_ORDER: Record<string, number> = {
    [ITEM_STATUS_CODES.inop]: 0,
    [ITEM_STATUS_CODES.warn]: 1,
    [ITEM_STATUS_CODES.under_review]: 2,
};

const getCurrentIssueStatusSortRank = (status: string) => CURRENT_ISSUE_STATUS_SORT_ORDER[status] ?? 99;

const compareEquipmentNames = (a: Discrepancy, b: Discrepancy) =>
    String(a.equipmentName ?? '').localeCompare(String(b.equipmentName ?? ''), undefined, {
        numeric: true,
        sensitivity: 'base',
    });

const compareCurrentIssuesDefault = (a: Discrepancy, b: Discrepancy) => {
    const statusCompare = getCurrentIssueStatusSortRank(a.status) - getCurrentIssueStatusSortRank(b.status);
    if (statusCompare !== 0) {
        return statusCompare;
    }
    return compareEquipmentNames(a, b);
};

export const Issues = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [discrepancies, setDiscrepancies] = useState<Discrepancy[]>([]);
    const [discrepancyToUpdate, setDiscrepancyToUpdate] = useState<Discrepancy | null>(null);
    const [equipmentToUpdate, setEquipmentToUpdate] = useState<tGroupedEquipment | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [archivedDiscrepanciesList, setArchivedDiscrepanciesList] = useState<ArchivedDiscrepancy[] | []>([]);
    const [archivedDiscrepancyToUpdateType, setArchivedDiscrepancyToUpdateType] = useState<ArchivedDiscrepancy | null>(
        null,
    );
    const [discrepancyToUpdateType, setDiscrepancyToUpdateType] = useState<Discrepancy | null>(null);

    const [equipmentById, setEquipmentById] = useState<Record<string, tGroupedEquipment | null>>({});

    const [updateStatus, setUpdateStatus] = useState<string | null>(null);
    const [shouldShowUpdateNotificationsModal, setShouldShowUpdateNotificationsModal] = useState<boolean>(
        searchParams.get(SEARCH_PARAMS.SETTINGS) === SEARCH_PARAMS.EMAIL,
    );
    const [updateNotes, setUpdateNotes] = useState('');
    const [isUpdating, setIsUpdating] = useState<string | null>(null);

    const [selectedLog, setSelectedLog] = useState<tDiscrepancyLog[] | null>(null);
    const [activeTab, setActiveTab] = useState<'current' | 'archived'>(() => parseIssuesListTab(searchParams));
    const [archivedSortColumnId, setArchivedSortColumnId] = useState(ARCHIVED_DEFAULT_SORT_COLUMN_ID);
    const [archivedSortDirection, setArchivedSortDirection] = useState<SortDirection>('desc');

    const { company, companyUser, companyOwner, businessAdmin, setCompanyUser, setCompanyOwner } = useUserContext();

    const {
        companyUnits,
        companyGroupedEquipmentData,
        loading: equipmentLoading,
        updateUnitInDatabase,
        refreshEquipment,
    } = useEquipmentContext();
    const { companyRegions, companyLocations: companyLocationsRaw, companyDistricts } = useRegionAndLocationContext();
    // Gen2 Location type omits the residual Gen1 `districtId`, but migrated records may still
    // carry it at runtime (see getInterimDistrictParentId). Widen locally without changing behavior.
    const companyLocations = companyLocationsRaw as (Location & { districtId?: string | null })[];
    const { setIssueCount } = useAppContext();

    const [emailNotificationInfo, SetEmailNotificationInfo] = useState<string | null>(
        companyUser?.discrepancyNotificationTypes ?? companyOwner?.discrepancyNotificationTypes ?? null,
    );

    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const [pageSize, _setPageSize] = useState(50);
    const [unitNameFilter, setUnitNameFilter] = useState('');
    const [filterStackFullWidth, setFilterStackFullWidth] = useState(false);
    const [filters, setFilters] = useState(() => ({
        region: localStorage.getItem(LOCAL_STORAGE_KEYS.ISSUE_FILTERED_BY_REGION)
            ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.ISSUE_FILTERED_BY_REGION)!)
            : null,
        location: localStorage.getItem(LOCAL_STORAGE_KEYS.ISSUE_FILTERED_BY_LOCATION)
            ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.ISSUE_FILTERED_BY_LOCATION)!)
            : null,
        equipment: localStorage.getItem(LOCAL_STORAGE_KEYS.ISSUE_FILTERED_BY_EQUIPMENT)
            ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.ISSUE_FILTERED_BY_EQUIPMENT)!)
            : ALL_EQUIPMENT,
        unit: localStorage.getItem(LOCAL_STORAGE_KEYS.ISSUE_FILTERED_BY_UNIT)
            ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.ISSUE_FILTERED_BY_UNIT)!)
            : null,
    }));

    const [locations, setLocations] = useState<{ label: string; value: string }[]>([]);
    const [regions, setRegions] = useState<{ label: string; value: string }[]>([]);

    const [newDiscrepancyNotificationTypes, setNewDiscrepancyNotificationTypes] = useState(
        `{ "inop": false, "warn": false }`,
    );

    const companyId = (company?.id as string) || companyOwner?.companyId;

    const unitNameById = useMemo(() => {
        const map = new Map<string, string>();
        for (const unit of companyUnits) {
            if (unit.id && unit.name) {
                map.set(unit.id, unit.name);
            }
        }
        return map;
    }, [companyUnits]);

    const canUpdateDiscrepancies = hasPermission(
        companyOwner,
        companyUser,
        Permission.UPDATE_DISCREPANCIES,
        businessAdmin,
    );

    /** Start-of-day reports have no real equipment record, so classify is not possible */
    const canClassifyItem = (equipmentId: string) => !!equipmentById[equipmentId];

    // ─── Data fetching (unchanged) ───

    useEffect(() => {
        if (!companyId) {
            return;
        }

        void fetchCompanyRegion();
    }, [companyId]);

    useEffect(() => {
        if (!companyId || equipmentLoading) {
            return;
        }

        void getCompanyEquipment();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, companyGroupedEquipmentData, equipmentLoading]);

    useEffect(() => {
        const initializeLocations = async () => {
            try {
                const regionValue = filters.region?.value;
                await fetchCompanyLocations(regionValue);
            } catch (error) {
                console.error('Error initializing locations:', error);
                Sentry.captureException(error);
            }
        };

        if (companyId) {
            initializeLocations();
        }
    }, [companyId, filters.region]);

    const getCompanyEquipment = async () => {
        if (!companyId) {
            return;
        }
        setIsLoading(true);
        try {
            const allEquipment = companyGroupedEquipmentData;
            const filteredEquipment = companyUser
                ? companyUser.globalLocationPermission
                    ? allEquipment
                    : allEquipment.filter((item) => {
                          if (item.units && item.units.length > 0) {
                              return item.units.some(
                                  (unit) =>
                                      !unit?.locationId || companyUser.locationAssignment?.includes(unit.locationId),
                              );
                          }
                          return true;
                      })
                : allEquipment;

            const equipObj: Record<string, tGroupedEquipment> = {};
            filteredEquipment.forEach((item) => (equipObj[item.id] = { ...item }));
            setEquipmentById(equipObj);
            await Promise.all([getDiscrepancies(equipObj), getArchivedDiscrepancies(undefined, equipObj)]);
        } catch (error) {
            console.error('Error fetching data:', error);
            Sentry.captureException(error);
        } finally {
            setIsLoading(false);
        }
    };

    const getDiscrepancies = async (equipObj?: Record<string, tGroupedEquipment | null>) => {
        if (!companyId) return;

        setIsLoading(true);
        try {
            const result = await authClient.graphql({
                query: listDiscrepancyByCompanyIdAndUpdatedAtQuery,
                variables: {
                    companyId: companyId,
                    sortDirection: 'DESC' as ModelSortDirection,
                },
            });

            const filteredDiscrepancies = companyUser
                ? result.data.listDiscrepancyByCompanyIdAndUpdatedAt.items.filter((item) => {
                      if (companyUser.globalLocationPermission) return true;

                      if (item.equipmentId === KEYS_START_OF_DAY_REPORT.EQUIPMENT_ID) {
                          return !!companyUser?.globalLocationPermission;
                      }

                      const equipment = equipObj ? equipObj[item.equipmentId] : equipmentById[item.equipmentId];
                      if (!equipment) return false;

                      if (!companyUser?.locationAssignment) return false;

                      if (item.unitId) {
                          const unit = equipment.units?.find((u) => u?.id === item.unitId);
                          if (!unit?.locationId) return true;
                          return companyUser.locationAssignment.includes(unit.locationId);
                      }
                      return true;
                  })
                : result.data.listDiscrepancyByCompanyIdAndUpdatedAt.items;

            // Resolved issues belong in Archived — migrate any leftovers and keep Current open-only
            const openIssues = filteredDiscrepancies.filter((item) => item.status !== ITEM_STATUS_CODES.ok);
            const resolvedToArchive = filteredDiscrepancies.filter((item) => item.status === ITEM_STATUS_CODES.ok);

            setDiscrepancies(openIssues);

            if (resolvedToArchive.length > 0) {
                // Archive in parallel with concurrency limit to avoid blocking page load.
                // Skip items that fail — they'll be retried on the next fetch.
                const BATCH_SIZE = 5;
                let archivedCount = 0;

                for (let i = 0; i < resolvedToArchive.length; i += BATCH_SIZE) {
                    const batch = resolvedToArchive.slice(i, i + BATCH_SIZE);
                    const results = await Promise.allSettled(batch.map((item) => archiveDiscrepancyRecord(item)));
                    archivedCount += results.filter((r) => r.status === 'fulfilled').length;
                    results
                        .filter((r) => r.status === 'rejected')
                        .forEach((r) => {
                            console.error('Error auto-archiving resolved issue:', (r as PromiseRejectedResult).reason);
                            Sentry.captureException((r as PromiseRejectedResult).reason);
                        });
                }

                if (archivedCount > 0) {
                    setIssueCount((prev) => Math.max(0, prev - archivedCount));
                    await getArchivedDiscrepancies(undefined, equipObj);
                }
            }
        } catch (error) {
            console.error('error fetching data:', error);
            Sentry.captureException(error);
        } finally {
            setIsLoading(false);
        }
    };

    const getArchivedDiscrepancies = async (
        equipmentId?: string,
        equipObj?: Record<string, tGroupedEquipment | null>,
    ) => {
        if (!companyId) {
            return;
        }

        setIsLoading(true);
        try {
            let result;
            if (!equipmentId || equipmentId === ALL_EQUIPMENT.value) {
                result = await authClient.graphql({
                    query: listArchivedDiscrepancyByCompanyIdAndUpdatedAt,
                    variables: {
                        companyId: companyId,
                        sortDirection: 'DESC' as ModelSortDirection,
                    },
                });

                const visibleEquipment = companyUser
                    ? result.data.listArchivedDiscrepancyByCompanyIdAndUpdatedAt.items.filter((item) => {
                          if (item.equipmentId === KEYS_START_OF_DAY_REPORT.EQUIPMENT_ID) {
                              return companyUser?.globalLocationPermission === true;
                          }
                          const equipment = equipObj ? equipObj[item.equipmentId] : equipmentById[item.equipmentId];
                          if (!equipment) {
                              return false;
                          }

                          if (!companyUser?.locationAssignment || companyUser.locationAssignment.length === 0) {
                              if (item.unitId) {
                                  const unit = equipment.units?.find((u) => u?.id === item.unitId);
                                  return !unit?.locationId;
                              }
                              return true;
                          }

                          if (item.unitId) {
                              const unit = equipment.units?.find((u) => u?.id === item.unitId);
                              if (!unit?.locationId) {
                                  return true;
                              }
                              return companyUser.locationAssignment.includes(unit.locationId);
                          }

                          return true;
                      })
                    : result.data.listArchivedDiscrepancyByCompanyIdAndUpdatedAt.items;
                setArchivedDiscrepanciesList(visibleEquipment);
            } else {
                result = await authClient.graphql({
                    query: listArchivedDiscrepancyByEquipmentIdAndUpdatedAt,
                    variables: {
                        equipmentId: equipmentId,
                        sortDirection: 'DESC' as ModelSortDirection,
                    },
                });
                const visibleEquipment = companyUser
                    ? result.data.listArchivedDiscrepancyByEquipmentIdAndUpdatedAt.items.filter((item) => {
                          const equipment = equipmentById[item.equipmentId];
                          if (!equipment) return false;

                          if (!companyUser?.locationAssignment || companyUser.locationAssignment.length === 0) {
                              if (item.unitId) {
                                  const unit = equipment.units?.find((u) => u?.id === item.unitId);
                                  return !unit?.locationId;
                              }
                              return true;
                          }

                          if (item.unitId) {
                              const unit = equipment.units?.find((u) => u?.id === item.unitId);
                              if (!unit?.locationId) return true;
                              return companyUser.locationAssignment.includes(unit.locationId);
                          }

                          return true;
                      })
                    : result.data.listArchivedDiscrepancyByEquipmentIdAndUpdatedAt.items;
                setArchivedDiscrepanciesList(visibleEquipment);
            }
        } catch (error) {
            console.log(error);
            Sentry.captureException(error);
        } finally {
            setIsLoading(false);
        }
    };

    // ─── Filtering helpers (unchanged) ───

    const getIssueRegionId = (issue: Discrepancy | ArchivedDiscrepancy) => {
        if (issue.equipmentId === KEYS_START_OF_DAY_REPORT.EQUIPMENT_ID) {
            return null;
        }

        const equipment = equipmentById[issue.equipmentId];
        if (!equipment || !issue.unitId) {
            return null;
        }

        const unit = equipment.units?.find((u) => u?.id === issue.unitId);
        if (!unit?.locationId) {
            return null;
        }

        const location = companyLocations.find((loc) => loc.id === unit.locationId);
        if (!location?.districtId) {
            return null;
        }

        const district = companyDistricts.find((d) => d.id === location.districtId);
        return district?.regionalDivisionId ?? null;
    };

    const getFilteredArchivedIssues = () => {
        let filteredIssues = archivedDiscrepanciesList;

        if (filters.region?.value && companyLocations.length > 0 && companyDistricts.length > 0) {
            filteredIssues = filteredIssues.filter((issue) => {
                if (issue.equipmentId === KEYS_START_OF_DAY_REPORT.EQUIPMENT_ID) {
                    return true;
                }

                const issueRegionId = getIssueRegionId(issue);
                if (!issueRegionId) {
                    return false;
                }

                return issueRegionId === filters.region.value;
            });
        }

        if (filters.equipment.value !== ALL_EQUIPMENT.value) {
            filteredIssues = filteredIssues.filter((issue) => issue.equipmentId === filters.equipment.value);
        }

        if (filters.location?.value) {
            filteredIssues = filteredIssues.filter((issue) => {
                if (issue.equipmentId === KEYS_START_OF_DAY_REPORT.EQUIPMENT_ID) return true;

                const equipment = equipmentById[issue.equipmentId];
                if (!equipment) return false;

                if (filters.location.value === 'unassigned') {
                    if (issue.unitId) {
                        const unit = equipment.units?.find((u) => u?.id === issue.unitId);
                        return !unit?.locationId;
                    } else {
                        return true;
                    }
                } else {
                    if (issue.unitId) {
                        const unit = equipment.units?.find((u) => u?.id === issue.unitId);
                        return unit?.locationId === filters.location.value;
                    } else {
                        return false;
                    }
                }
            });
        }

        if (filters.unit?.value) {
            filteredIssues = filteredIssues.filter((issue) => issue.unitId === filters.unit.value);
        }

        return filteredIssues;
    };

    const filteredArchivedIssues = useMemo(
        () => filterIssuesByUnitName(getFilteredArchivedIssues(), unitNameFilter, unitNameById),
        [archivedDiscrepanciesList, filters, companyLocations, companyDistricts, equipmentById, unitNameFilter, unitNameById],
    );

    useEffect(() => {
        setActiveTab(parseIssuesListTab(searchParams));
    }, [searchParams]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 688px)');
        const syncFilterLayout = () => setFilterStackFullWidth(mediaQuery.matches);

        syncFilterLayout();
        mediaQuery.addEventListener('change', syncFilterLayout);

        return () => mediaQuery.removeEventListener('change', syncFilterLayout);
    }, []);

    useEffect(() => {
        const issueId = searchParams.get(SEARCH_PARAMS.ISSUE_ID);
        if (!issueId) {
            return;
        }

        navigate(`/issue/${issueId}`, { replace: true });
    }, [navigate, searchParams]);

    const updateFilter = async (filterType: string, value: any) => {
        const newFilters = { ...filters, [filterType]: value };

        if (filterType === 'region') {
            newFilters.location = null;
            newFilters.equipment = ALL_EQUIPMENT;
            newFilters.unit = null;
            if (!value || value.value === '') {
                localStorage.removeItem(LOCAL_STORAGE_KEYS.ISSUE_FILTERED_BY_LOCATION);
                localStorage.removeItem(LOCAL_STORAGE_KEYS.ISSUE_FILTERED_BY_EQUIPMENT);
            }
            await fetchCompanyLocations(value?.value);
        } else if (filterType === 'location') {
            newFilters.equipment = ALL_EQUIPMENT;
            newFilters.unit = null;
            localStorage.removeItem(LOCAL_STORAGE_KEYS.ISSUE_FILTERED_BY_EQUIPMENT);
        } else if (filterType === 'equipment') {
            newFilters.unit = null;
        }

        setFilters(newFilters);
        setCurrentPageIndex(1);

        if (value && value.value !== '') {
            localStorage.setItem(
                LOCAL_STORAGE_KEYS[`ISSUE_FILTERED_BY_${filterType.toUpperCase()}` as keyof typeof LOCAL_STORAGE_KEYS],
                JSON.stringify(value),
            );
        } else {
            localStorage.removeItem(
                LOCAL_STORAGE_KEYS[`ISSUE_FILTERED_BY_${filterType.toUpperCase()}` as keyof typeof LOCAL_STORAGE_KEYS],
            );
        }
    };

    const getFilteredEquipment = () => {
        let filteredEquipment = companyGroupedEquipmentData;

        if (filters.location?.value) {
            filteredEquipment = filteredEquipment.filter((equipment) => {
                if (filters.location.value === 'unassigned') {
                    return equipment.units?.some((unit) => !unit?.locationId);
                } else {
                    return equipment.units?.some((unit) => unit?.locationId === filters.location.value);
                }
            });
        }

        return filteredEquipment;
    };

    const getUnitsForEquipment = (equipment: tSelectLabelValue) => {
        const selectedEquipment = equipmentById[equipment.value];
        if (!selectedEquipment?.units?.length) return [];

        return selectedEquipment.units.filter((unit) => {
            if (!filters.location?.value) return true;
            if (filters.location.value === 'unassigned') {
                return !unit?.locationId;
            }
            return unit?.locationId === filters.location.value;
        });
    };

    const fetchCompanyRegion = async () => {
        if (!companyId) {
            return;
        }
        setIsLoading(true);

        try {
            const currentCompanyRegions = companyRegions
                .filter((region) => region.companyId === companyId)
                .map((region) => ({
                    label: region.name,
                    value: region.id,
                }));

            const filteredRegions = companyUser
                ? currentCompanyRegions.filter((item) => {
                      if (!companyUser.regionalAssignment) {
                          return false;
                      }
                      if (Array.isArray(companyUser.regionalAssignment)) {
                          return companyUser.regionalAssignment.includes(item.value);
                      }
                      return item.value === companyUser.regionalAssignment;
                  })
                : currentCompanyRegions;

            setRegions([{ label: 'All Regions', value: '' }, ...filteredRegions]);
        } catch (error) {
            console.error('Error fetching data:', error);
            Sentry.captureException(error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCompanyLocations = async (regionId?: string) => {
        try {
            let scopedLocations = companyLocations;

            if (regionId) {
                const districtIds = new Set(
                    companyDistricts
                        .filter((district) => district.regionalDivisionId === regionId)
                        .map((district) => district.id),
                );
                scopedLocations = companyLocations.filter(
                    (location) => location.districtId != null && districtIds.has(location.districtId),
                );
            }

            const locationOptions = scopedLocations.map((location) => {
                const district = companyDistricts.find((d) => d.id === location.districtId);
                const label = district ? `${location.name} (${district.name})` : location.name;
                return { label, value: location.id };
            });

            const filteredLocations = companyUser
                ? locationOptions.filter((item) => {
                      if (!companyUser.locationAssignment) {
                          return false;
                      }
                      if (Array.isArray(companyUser.locationAssignment)) {
                          return companyUser.locationAssignment.includes(item.value);
                      }
                      return item.value === companyUser.locationAssignment;
                  })
                : locationOptions;

            const newLocations = [{ label: 'All Locations', value: '' }, ...filteredLocations];
            setLocations(newLocations);
            return newLocations;
        } catch (error) {
            console.error('Error fetching locations:', error);
            Sentry.captureException(error);
            return [];
        }
    };

    // ─── Mutations ───

    const updateDiscrepancyStatus = async () => {
        if (!discrepancyToUpdate) return;
        setIsUpdating(discrepancyToUpdate.id);
        setIsLoading(true);

        const nextStatus = updateStatus || discrepancyToUpdate.status;
        const nextLog = JSON.stringify([
            ...JSON.parse(discrepancyToUpdate.log || '[]'),
            {
                name: companyUser?.name || companyOwner?.name,
                notes: `${updateNotes}${
                    updateStatus
                        ? ` [Updated Status: ${ITEM_STATUS_CONTENT[updateStatus as keyof typeof ITEM_STATUS_CONTENT]}] `
                        : ''
                }`,
                date: new Date().toISOString(),
            },
        ]);

        try {
            // Resolving archives (create + delete) with the final status/log — skip the
            // intermediate update so we don't race an updated-then-deleted record.
            if (updateStatus !== ITEM_STATUS_CODES.ok) {
                await authClient.graphql({
                    query: updateDiscrepancyMutation,
                    variables: {
                        input: {
                            id: discrepancyToUpdate.id,
                            status: nextStatus,
                            log: nextLog,
                        },
                    },
                });
            }

            if (
                updateStatus &&
                discrepancyToUpdate.promptTitle &&
                discrepancyToUpdate.promptLabel &&
                discrepancyToUpdate.promptDescription
            ) {
                const reportResult = await authClient.graphql({
                    query: getReportQuery,
                    variables: {
                        id: discrepancyToUpdate.reportId ?? '',
                    },
                });

                if (reportResult.data.getReport) {
                    const report = reportResult.data.getReport;
                    const userData = JSON.parse(report.userData || '[]');

                    const updatedUserData = userData.map((item: any) => {
                        if (
                            item.title === discrepancyToUpdate.promptTitle &&
                            item.label === discrepancyToUpdate.promptLabel &&
                            item.description === discrepancyToUpdate.promptDescription
                        ) {
                            if (updateStatus === ITEM_STATUS_CODES.ok) {
                                return {
                                    ...item,
                                    value: updateStatus,
                                };
                            }
                            return item;
                        }
                        return item;
                    });

                    await authClient.graphql({
                        query: updateReportMutation,
                        variables: {
                            input: {
                                id: report.id,
                                userData: JSON.stringify(updatedUserData),
                            },
                        },
                    });
                }
            }

            // Resolved issues leave Current and move to Archived
            if (updateStatus === ITEM_STATUS_CODES.ok) {
                await archiveDiscrepancyRecord({
                    ...discrepancyToUpdate,
                    status: ITEM_STATUS_CODES.ok,
                    log: nextLog,
                });
                setIssueCount((prev) => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.log(error);
            Sentry.captureException(error);
        } finally {
            setIsUpdating(null);
            setUpdateNotes('');
            setUpdateStatus(null);
            setDiscrepancyToUpdate(null);
            setIsLoading(false);
            try {
                await Promise.all([getDiscrepancies(), getArchivedDiscrepancies(undefined, undefined)]);
            } catch (refreshError) {
                console.error('Error refreshing data:', refreshError);
                Sentry.captureException(refreshError);
            }
        }
    };

    const updateEquipmentAndDiscrepancy = async () => {
        if (!equipmentToUpdate || (!discrepancyToUpdateType && !archivedDiscrepancyToUpdateType)) return;
        setIsUpdating(
            discrepancyToUpdateType
                ? discrepancyToUpdateType.id
                : archivedDiscrepancyToUpdateType
                  ? archivedDiscrepancyToUpdateType.id
                  : null,
        );
        setIsLoading(true);
        try {
            const discrepancyId = discrepancyToUpdateType?.id || archivedDiscrepancyToUpdateType?.id;
            const unitId = discrepancyToUpdateType?.unitId || archivedDiscrepancyToUpdateType?.unitId;

            if (unitId) {
                try {
                    const unitToUpdate = companyUnits.find((unit) => unit.id === unitId);
                    if (unitToUpdate) {
                        const currentDownReason = unitToUpdate.downReason ? JSON.parse(unitToUpdate.downReason) : [];

                        const updatedDownReason = JSON.stringify([
                            ...currentDownReason,
                            {
                                reason: updateStatus,
                                date: new Date().toISOString(),
                            },
                        ]);

                        await updateUnitInDatabase(unitId, { downReason: updatedDownReason });
                    }
                } catch (equipmentError) {
                    console.error('Error updating unit:', equipmentError);
                    Sentry.captureException(equipmentError);
                    throw equipmentError;
                }
            }

            if (discrepancyToUpdateType) {
                try {
                    await authClient.graphql({
                        query: updateDiscrepancyMutation,
                        variables: {
                            input: {
                                id: discrepancyId ?? '',
                                downReason: updateStatus,
                            },
                        },
                    });
                } catch (discrepancyError) {
                    console.error('Error updating discrepancy:', discrepancyError);
                    Sentry.captureException(discrepancyError);
                    throw discrepancyError;
                }
            } else if (archivedDiscrepancyToUpdateType) {
                try {
                    await authClient.graphql({
                        query: updateArchivedDiscrepancy,
                        variables: {
                            input: {
                                id: discrepancyId ?? '',
                                downReason: updateStatus,
                            },
                        },
                    });
                } catch (archivedDiscrepancyError) {
                    console.error('Error updating discrepancy:', archivedDiscrepancyError);
                    Sentry.captureException(archivedDiscrepancyError);
                    throw archivedDiscrepancyError;
                }
            }
        } catch (error) {
            console.log(error);
            Sentry.captureException(error);
        } finally {
            setIsUpdating(null);
            setUpdateStatus(null);
            setDiscrepancyToUpdateType(null);
            setArchivedDiscrepancyToUpdateType(null);
            setEquipmentToUpdate(null);
            setIsLoading(false);

            try {
                await refreshEquipment();
                getCompanyEquipment();
            } catch (refreshError) {
                console.error('Error refreshing data:', refreshError);
                Sentry.captureException(refreshError);
            }
        }
    };

    const updateEmailNotifications = async () => {
        try {
            const notifications = JSON.parse(newDiscrepancyNotificationTypes);
            const hasAnyNotifications = notifications?.inop || notifications?.warn;

            companyOwner
                ? (async () => {
                      const companyOwnerResponse = await authClient.graphql({
                          query: updateCompanyOwner,
                          variables: {
                              input: {
                                  id: companyOwner.id,
                                  discrepancyNotificationTypes: newDiscrepancyNotificationTypes,
                                  discrepancyNotification: hasAnyNotifications,
                              },
                          },
                      });

                      setCompanyOwner(companyOwnerResponse.data.updateCompanyOwner);
                      SetEmailNotificationInfo(
                          companyOwnerResponse.data.updateCompanyOwner.discrepancyNotificationTypes || null,
                      );
                  })()
                : companyUser
                  ? (async () => {
                        const companyUserResponse = await authClient.graphql({
                            query: updateUser,
                            variables: {
                                input: {
                                    id: companyUser.id,
                                    discrepancyNotificationTypes: newDiscrepancyNotificationTypes,
                                    discrepancyNotification: hasAnyNotifications,
                                },
                            },
                        });

                        setCompanyUser(companyUserResponse.data.updateUser);
                        SetEmailNotificationInfo(
                            companyUserResponse.data.updateUser.discrepancyNotificationTypes || null,
                        );
                    })()
                  : null;
        } catch (error) {
            console.log(error);
            Sentry.captureException(error);
        } finally {
            setShouldShowUpdateNotificationsModal(false);
            try {
                getDiscrepancies();
            } catch (refreshError) {
                console.error('Error refreshing data:', refreshError);
                Sentry.captureException(refreshError);
            }
        }
    };

    useEffect(() => {
        if (shouldShowUpdateNotificationsModal) {
            const userNotificationTypes =
                companyUser?.discrepancyNotificationTypes || companyOwner?.discrepancyNotificationTypes;
            setNewDiscrepancyNotificationTypes(userNotificationTypes || `{ "inop": false, "warn": false }`);
        }
    }, [shouldShowUpdateNotificationsModal, companyUser, companyOwner]);

    // ─── UI helpers ───

    const getStatusOptions = () => {
        switch (discrepancyToUpdate?.status) {
            case ITEM_STATUS_CODES.inop:
                return [
                    { value: '', label: ITEM_STATUS_CONTENT.no_change },
                    { value: ITEM_STATUS_CODES.under_review, label: `Mark as ${ITEM_STATUS_CONTENT.under_review}` },
                    { value: ITEM_STATUS_CODES.warn, label: `Mark as ${ITEM_STATUS_CONTENT.warn}` },
                    { value: ITEM_STATUS_CODES.ok, label: `Mark as ${ITEM_STATUS_CONTENT.resolved}` },
                ];
            case ITEM_STATUS_CODES.warn:
                return [
                    { value: '', label: ITEM_STATUS_CONTENT.no_change },
                    { value: ITEM_STATUS_CODES.under_review, label: `Mark as ${ITEM_STATUS_CONTENT.under_review}` },
                    { value: ITEM_STATUS_CODES.ok, label: `Mark as ${ITEM_STATUS_CONTENT.resolved}` },
                ];
            case ITEM_STATUS_CODES.under_review:
                return [
                    { value: '', label: ITEM_STATUS_CONTENT.no_change },
                    { value: ITEM_STATUS_CODES.warn, label: `Mark as ${ITEM_STATUS_CONTENT.warn}` },
                    { value: ITEM_STATUS_CODES.inop, label: `Mark as ${ITEM_STATUS_CONTENT.inop}` },
                    { value: ITEM_STATUS_CODES.ok, label: `Mark as ${ITEM_STATUS_CONTENT.resolved}` },
                ];
            default:
                return [
                    { value: '', label: ITEM_STATUS_CONTENT.no_change },
                    { value: ITEM_STATUS_CODES.ok, label: `Mark as ${ITEM_STATUS_CONTENT.resolved}` },
                ];
        }
    };

    const getDownReasonOptions = () => {
        return [
            { value: 'Preventable', label: 'Preventable' },
            { value: 'Normal Wear and Tear', label: 'Normal Wear and Tear' },
            { value: 'Service Item', label: 'Service Item' },
        ];
    };

    const getStatusClass = (status: string) => {
        switch (status) {
            case ITEM_STATUS_CODES.inop:
                return 'status-inop';
            case ITEM_STATUS_CODES.warn:
                return 'status-warn';
            case ITEM_STATUS_CODES.under_review:
                return 'status-under-review';
            default:
                return 'status-ok';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case ITEM_STATUS_CODES.inop:
                return ITEM_STATUS_CONTENT.inop;
            case ITEM_STATUS_CODES.warn:
                return ITEM_STATUS_CONTENT.warn;
            case ITEM_STATUS_CODES.under_review:
                return ITEM_STATUS_CONTENT.under_review;
            default:
                return ITEM_STATUS_CONTENT.resolved;
        }
    };

    const renderReferencedComponent = (item: Discrepancy | ArchivedDiscrepancy) => (
        <div className="ref-component">
            <div>
                <span className="ref-title">{item.promptTitle}</span>
                {item.promptLabel && <span className="ref-label"> -{item.promptLabel}</span>}
            </div>
            {item.promptDescription && <span className="ref-desc">{item.promptDescription}</span>}
        </div>
    );

    const renderStatusCell = (status: string) => (
        <span className={`status-badge ${getStatusClass(status)}`}>{getStatusLabel(status)}</span>
    );

    const handleUnitNameFilterChange = (value: string) => {
        setUnitNameFilter(value);
        setCurrentPageIndex(1);
    };

    const renderUnitNameFilter = () => (
        <input
            type="search"
            value={unitNameFilter}
            placeholder="Type to filter by Unit Name / ID"
            aria-label="Type to filter by Unit Name / ID"
            onChange={(event) => handleUnitNameFilterChange(event.target.value)}
        />
    );

    const renderEquipmentNameCell = useCallback(
        (item: { equipmentId: string; unitId?: string | null; equipmentName?: string | null }) => {
            const label = item.equipmentName || 'Unknown Equipment';
            const unitName = item.unitId ? unitNameById.get(item.unitId) : undefined;
            const detailPath = buildIssueEquipmentDetailPath(item.equipmentId, item.unitId, equipmentById);

            const equipmentLabel = detailPath ? (
                <Btn
                    buttonAs="a"
                    variant="link"
                    buttonClasses="issues-equipment-link"
                    buttonText={label}
                    href={detailPath}
                    buttonOnClick={(event) => {
                        event.preventDefault();
                        navigate(detailPath);
                    }}
                />
            ) : (
                <span className="issues-equipment-name">{label}</span>
            );

            return (
                <div className="issues-equipment-cell">
                    {equipmentLabel}
                    {unitName && <span className="issues-equipment-unit-hint">{unitName}</span>}
                </div>
            );
        },
        [equipmentById, navigate, unitNameById],
    );

    const renderCurrentIssueActions = useCallback(
        (item: Discrepancy) => {
            const canClassify = !item.downReason && canClassifyItem(item.equipmentId);
            const classifyDisabledTooltip = item.downReason ? 'Already classified' : undefined;

            return (
                <div className="table-actions">
                    <BtnIconOnly
                        size="sm"
                        icon={<IconListSquare />}
                        ariaLabel={`View log for ${item.equipmentName ?? 'issue'}`}
                        onClick={() => setSelectedLog(JSON.parse(item.log || '[]'))}
                    />

                    {canUpdateDiscrepancies && (
                        <BtnIconOnly
                            size="sm"
                            icon={<IconPencil />}
                            ariaLabel={`Update status for ${item.equipmentName ?? 'issue'}`}
                            onClick={() => setDiscrepancyToUpdate(item)}
                            disabled={isUpdating === item.id}
                            buttonClasses={isUpdating === item.id ? 'loading' : undefined}
                        />
                    )}
                    <BtnIconOnly
                        size="sm"
                        icon={<IconAlertTriangle />}
                        ariaLabel={`View issue for ${item.equipmentName ?? 'issue'}`}
                        onClick={() => navigate(`/issue/${item.id}`)}
                    />
                    {canUpdateDiscrepancies && (
                        <BtnIconOnly
                            size="sm"
                            icon={<IconClipboard />}
                            ariaLabel={
                                classifyDisabledTooltip ??
                                `Classify issue type for ${item.equipmentName ?? 'issue'}`
                            }
                            buttonTooltip={classifyDisabledTooltip}
                            disabled={!canClassify}
                            onClick={() => {
                                const currentEquipment = equipmentById[item.equipmentId];
                                setEquipmentToUpdate(currentEquipment);
                                setDiscrepancyToUpdateType(item);
                            }}
                        />
                    )}
                </div>
            );
        },
        [canUpdateDiscrepancies, equipmentById, isUpdating, navigate],
    );

    const renderArchivedIssueActions = useCallback(
        (item: ArchivedDiscrepancy) => {
            const canClassify = !item.downReason && canClassifyItem(item.equipmentId);
            const classifyDisabledTooltip = item.downReason ? 'Already classified' : undefined;

            return (
                <div className="table-actions">
                    <BtnIconOnly
                        size="sm"
                        icon={<IconListSquare />}
                        ariaLabel={`View log for ${item.equipmentName ?? 'issue'}`}
                        onClick={() => setSelectedLog(JSON.parse(item.log || '[]'))}
                    />
                    <BtnIconOnly
                        size="sm"
                        icon={<IconAlertTriangle />}
                        ariaLabel={`View issue for ${item.equipmentName ?? 'issue'}`}
                        onClick={() => navigate(`/issue/${item.id}`)}
                    />
                    {canUpdateDiscrepancies && (
                        <BtnIconOnly
                            size="sm"
                            icon={<IconClipboard />}
                            ariaLabel={
                                classifyDisabledTooltip ??
                                `Classify issue type for ${item.equipmentName ?? 'issue'}`
                            }
                            buttonTooltip={classifyDisabledTooltip}
                            disabled={!canClassify}
                            onClick={() => {
                                const currentEquipment = equipmentById[item.equipmentId];
                                setEquipmentToUpdate(currentEquipment);
                                setArchivedDiscrepancyToUpdateType(item);
                            }}
                        />
                    )}
                </div>
            );
        },
        [canUpdateDiscrepancies, equipmentById, navigate],
    );

    const currentIssueColumns = useMemo<DataTableColumn<Discrepancy>[]>(
        () => [
            {
                id: 'equipment',
                header: 'Equipment',
                sortable: true,
                sortType: 'alpha',
                sortValue: (item) => item.equipmentName ?? '',
                filterClassName: 'issues-table-filter-unit',
                filterRowClassName: 'data-table-th-filter-field',
                filterLabel: 'Filter by Unit Name / ID',
                filterColSpan: ISSUES_TABLE_COLUMN_COUNT,
                mobileCardPrimary: true,
                renderFilter: renderUnitNameFilter,
                renderCell: (item) => renderEquipmentNameCell(item),
            },
            {
                id: 'status',
                header: 'Status',
                align: 'center',
                sortable: true,
                sortType: 'numeric',
                sortValue: (item) => getCurrentIssueStatusSortRank(item.status),
                tdClassName: 'data-table-col-narrow',
                mobileCardStacked: true,
                renderCell: (item) => renderStatusCell(item.status),
            },
            {
                id: 'component',
                header: 'Referenced Component',
                sortable: true,
                sortType: 'alpha',
                sortValue: (item) => item.promptTitle ?? '',
                mobileCardStacked: true,
                renderCell: (item) => renderReferencedComponent(item),
            },
            {
                id: 'date',
                header: 'Date',
                sortable: true,
                sortType: 'numeric',
                sortValue: (item) => getIssueDateSortValue(item.updatedAt),
                mobileCardStacked: true,
                renderCell: (item) => formatIssueDate(item.updatedAt),
            },
            {
                id: 'actions',
                header: 'Actions',
                align: 'center',
                thClassName: 'col-actions data-table-col-narrow',
                tdClassName: 'col-actions data-table-col-narrow',
                mobileCardAction: true,
                mobileHideLabel: true,
                renderCell: (item) => renderCurrentIssueActions(item),
            },
        ],
        [renderCurrentIssueActions, renderEquipmentNameCell, unitNameFilter],
    );

    const sortedCurrentDiscrepancies = useMemo(
        () => filterIssuesByUnitName([...discrepancies].sort(compareCurrentIssuesDefault), unitNameFilter, unitNameById),
        [discrepancies, unitNameFilter, unitNameById],
    );

    const archivedIssueColumns = useMemo<DataTableColumn<ArchivedDiscrepancy>[]>(
        () => [
            {
                id: 'equipment',
                header: 'Equipment',
                sortable: true,
                sortType: 'alpha',
                sortValue: (item) => item.equipmentName ?? '',
                filterClassName: 'issues-table-filter-unit',
                filterRowClassName: 'data-table-th-filter-field',
                filterLabel: 'Filter by Unit Name / ID',
                filterColSpan: ISSUES_TABLE_COLUMN_COUNT,
                mobileCardPrimary: true,
                renderFilter: renderUnitNameFilter,
                renderCell: (item) => renderEquipmentNameCell(item),
            },
            {
                id: 'status',
                header: 'Status',
                align: 'center',
                sortable: true,
                sortType: 'alpha',
                sortValue: (item) => getStatusLabel(item.status),
                tdClassName: 'data-table-col-narrow',
                mobileCardStacked: true,
                renderCell: (item) => renderStatusCell(item.status),
            },
            {
                id: 'component',
                header: 'Referenced Component',
                sortable: true,
                sortType: 'alpha',
                sortValue: (item) => item.promptTitle ?? '',
                mobileCardStacked: true,
                renderCell: (item) => renderReferencedComponent(item),
            },
            {
                id: 'archivedDate',
                header: 'Date Archived',
                sortable: true,
                sortType: 'numeric',
                sortValue: (item) => getIssueDateSortValue(item.archivedDate),
                mobileCardStacked: true,
                renderCell: (item) => formatIssueDate(item.archivedDate),
            },
            {
                id: 'actions',
                header: 'Actions',
                align: 'center',
                thClassName: 'col-actions data-table-col-narrow',
                tdClassName: 'col-actions data-table-col-narrow',
                mobileCardAction: true,
                mobileHideLabel: true,
                renderCell: (item) => renderArchivedIssueActions(item),
            },
        ],
        [renderArchivedIssueActions, renderEquipmentNameCell, unitNameFilter],
    );

    const sortedArchivedIssues = useMemo(() => {
        const sortValue = (item: ArchivedDiscrepancy): string | number => {
            switch (archivedSortColumnId) {
                case 'equipment':
                    return item.equipmentName ?? '';
                case 'archivedDate':
                    return getIssueDateSortValue(item.archivedDate);
                case 'status':
                    return getStatusLabel(item.status);
                case 'component':
                    return item.promptTitle ?? '';
                default:
                    return getIssueDateSortValue(item.archivedDate);
            }
        };

        const sortType = archivedSortColumnId === 'archivedDate' ? 'numeric' : 'alpha';

        return [...filteredArchivedIssues].sort((a, b) =>
            compareArchivedIssueRows(a, b, sortValue, sortType, archivedSortDirection),
        );
    }, [archivedSortColumnId, archivedSortDirection, filteredArchivedIssues]);

    const archivedPageItems = useMemo(() => {
        const totalPages = Math.max(1, Math.ceil(sortedArchivedIssues.length / pageSize));
        const pageIndex = Math.min(currentPageIndex, totalPages);
        const startIndex = (pageIndex - 1) * pageSize;
        return sortedArchivedIssues.slice(startIndex, startIndex + pageSize);
    }, [currentPageIndex, pageSize, sortedArchivedIssues]);

    const notificationsAreOn = (() => {
        const notifications = emailNotificationInfo ? JSON.parse(emailNotificationInfo) : {};
        return notifications?.warn || notifications?.inop;
    })();

    const totalPages = Math.ceil(filteredArchivedIssues.length / pageSize);

    // ─── Render ───

    return (
        <div className="page-container issues-page">
            {/* Header */}
            <PageHeader
                title="Issues"
                actions={
                    <Btn
                        buttonClasses="btn-notifications"
                        variant={notificationsAreOn ? 'secondary-outlined' : 'warning-outlined'}
                        buttonIcon={<IconAlertTriangle iconClasses="notif-icon" />}
                        buttonText={
                            <>
                                Email Notifications:{' '}
                                <span className={notificationsAreOn ? 'notif-status-on' : 'notif-status-off'}>
                                    {notificationsAreOn ? 'On' : 'Off'}
                                </span>
                            </>
                        }
                        buttonOnClick={() => setShouldShowUpdateNotificationsModal(true)}
                    />
                }
            />

            {/* Card with tabs + table */}
            <div className="tab-component">
                {/* Tabs */}
                <TabNav
                    variant="tabs"
                    ariaLabel="Issues"
                    className="issues-tabs"
                    tabButtonClasses="issues-tab"
                    tabs={[
                        {
                            id: 'current',
                            label: `Current Issues (${discrepancies.length})`,
                        },
                        {
                            id: 'archived',
                            label: 'Archived Issues',
                        },
                    ]}
                    activeTabId={activeTab}
                    onTabChange={(tabId) => {
                        const nextTab = tabId as 'current' | 'archived';
                        setActiveTab(nextTab);
                        setSearchParams(
                            (prev) => {
                                const next = new URLSearchParams(prev);
                                if (nextTab === 'archived') {
                                    next.set(ISSUES_TAB_QUERY.param, ISSUES_TAB_QUERY.archived);
                                } else {
                                    next.delete(ISSUES_TAB_QUERY.param);
                                }
                                return next;
                            },
                            { replace: true },
                        );
                        if (nextTab === 'archived') {
                            setCurrentPageIndex(1);
                        }
                    }}
                    flared={true}
                />

                {/* Current Issues Tab */}
                {activeTab === 'current' && (
                    <Card className="card-lg tab-body issues-table-container">
                        {isLoading ? (
                            <div className="issues-loading" />
                        ) : (
                            <DataTable
                                caption="Current issues"
                                captionVisuallyHidden
                                tableClassName="issues-table"
                                columns={currentIssueColumns}
                                rows={sortedCurrentDiscrepancies}
                                getRowKey={(item) => item.id}
                                filterable
                                filterStackFullWidth={filterStackFullWidth}
                                hideHeaderWhenEmpty
                                emptyState={
                                    unitNameFilter.trim() ? (
                                        'No issues match your filters.'
                                    ) : (
                                        <div className="issues-empty">
                                            <b>No issues</b>
                                        </div>
                                    )
                                }
                            />
                        )}
                    </Card>
                )}

                {/* Archived Issues Tab */}
                {activeTab === 'archived' && (
                    <Card className="card-lg tab-body">
                        {/* Filters */}
                        <div className="archived-filters">
                            <select
                                value={filters.region?.value || ''}
                                onChange={(e) => {
                                    const opt = regions.find((r) => r.value === e.target.value);
                                    updateFilter('region', opt || null);
                                }}
                            >
                                {regions.map((r) => (
                                    <option key={r.value} value={r.value}>
                                        {r.label}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={filters.location?.value || ''}
                                onChange={(e) => {
                                    const opt = locations.find((l) => l.value === e.target.value);
                                    updateFilter('location', opt || null);
                                }}
                            >
                                {locations.map((l) => (
                                    <option key={l.value} value={l.value}>
                                        {l.label}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={filters.equipment?.value || ALL_EQUIPMENT.value}
                                onChange={(e) => {
                                    const startOfDayOpt = {
                                        label: KEYS_START_OF_DAY_REPORT.EQUIPMENT_NAME,
                                        value: KEYS_START_OF_DAY_REPORT.EQUIPMENT_ID,
                                    };
                                    const allOpts = [
                                        ALL_EQUIPMENT,
                                        startOfDayOpt,
                                        ...getFilteredEquipment().map((eq) => ({ label: eq.name, value: eq.id })),
                                    ];
                                    const opt = allOpts.find((o) => o.value === e.target.value);
                                    updateFilter('equipment', opt || ALL_EQUIPMENT);
                                }}
                            >
                                <option value={ALL_EQUIPMENT.value}>{ALL_EQUIPMENT.label}</option>
                                <option value={KEYS_START_OF_DAY_REPORT.EQUIPMENT_ID}>
                                    {KEYS_START_OF_DAY_REPORT.EQUIPMENT_NAME}
                                </option>
                                {getFilteredEquipment().map((eq) => (
                                    <option key={eq.id} value={eq.id}>
                                        {eq.name}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={filters.unit?.value || ''}
                                onChange={(e) => {
                                    const units = getUnitsForEquipment(filters.equipment);
                                    const allOpts = [
                                        { label: 'All Units', value: '' },
                                        ...units.map((u) => ({ label: u?.name || '', value: u?.id || '' })),
                                    ];
                                    const opt = allOpts.find((o) => o.value === e.target.value);
                                    updateFilter('unit', opt || null);
                                }}
                                disabled={!getUnitsForEquipment(filters.equipment).length}
                            >
                                <option value="">All Units</option>
                                {getUnitsForEquipment(filters.equipment).map((unit) => (
                                    <option key={unit?.id} value={unit?.id}>
                                        {unit?.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Pagination
                                className="issues-pagination"
                                currentPage={currentPageIndex}
                                totalPages={totalPages}
                                onPageChange={setCurrentPageIndex}
                                ariaLabel="Archived issues pagination"
                            />
                        )}

                        {isLoading ? (
                            <div className="issues-loading" />
                        ) : (
                            <DataTable
                                caption="Archived issues"
                                captionVisuallyHidden
                                tableClassName="issues-table"
                                columns={archivedIssueColumns}
                                rows={archivedPageItems}
                                getRowKey={(item) => item.id}
                                filterable
                                filterStackFullWidth={filterStackFullWidth}
                                hideHeaderWhenEmpty
                                defaultSortColumnId={ARCHIVED_DEFAULT_SORT_COLUMN_ID}
                                defaultSortDirection="desc"
                                sortColumnId={archivedSortColumnId}
                                sortDirection={archivedSortDirection}
                                onSortChange={(columnId, direction) => {
                                    setArchivedSortColumnId(columnId);
                                    setArchivedSortDirection(direction);
                                    setCurrentPageIndex(1);
                                }}
                                emptyState={
                                    unitNameFilter.trim() ? (
                                        'No archived issues match your filters.'
                                    ) : (
                                        <div className="issues-empty">
                                            <b>No archived issues</b>
                                        </div>
                                    )
                                }
                            />
                        )}
                    </Card>
                )}
            </div>

            {/* ─── Modal: Issue History ─── */}
            {selectedLog !== null && (
                <div className="issues-modal-overlay" onClick={() => setSelectedLog(null)}>
                    <div className="issues-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="issues-modal-header">
                            <h2>Issue History</h2>
                        </div>
                        <div className="issues-modal-body">
                            {selectedLog.length === 0 ? (
                                <p style={{ color: 'var(--slate-gray)', fontSize: 14 }}>No history entries.</p>
                            ) : (
                                selectedLog.map((logEntry: tDiscrepancyLog, idx: number) => (
                                    <div className="log-entry" key={idx}>
                                        <div className="log-notes">{logEntry.notes}</div>
                                        <div className="log-author">{logEntry.name}</div>
                                        <div className="log-date">
                                            {`${new Date(logEntry.date).toLocaleDateString()} ${new Date(logEntry.date).toLocaleTimeString()}`}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="issues-modal-footer">
                            <button className="btn-modal-cancel" onClick={() => setSelectedLog(null)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Modal: Update Status ─── */}
            {!!discrepancyToUpdate && !equipmentToUpdate && (
                <div className="issues-modal-overlay" onClick={() => setDiscrepancyToUpdate(null)}>
                    <div className="issues-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="issues-modal-header">
                            <h2>Update {discrepancyToUpdate.equipmentName}</h2>
                        </div>
                        <div className="issues-modal-body">
                            <div className="issues-radio-group">
                                {getStatusOptions().map((opt) => (
                                    <div className="issues-radio-option" key={opt.value}>
                                        <input
                                            type="radio"
                                            id={`status-${opt.value}`}
                                            name="updateStatus"
                                            value={opt.value}
                                            checked={(updateStatus || '') === opt.value}
                                            onChange={(e) =>
                                                setUpdateStatus(e.target.value.length ? e.target.value : null)
                                            }
                                        />
                                        <label htmlFor={`status-${opt.value}`}>{opt.label}</label>
                                    </div>
                                ))}
                            </div>
                            <label className="issues-field-label" htmlFor="issues-update-notes">Notes</label>
                            <textarea
                                id="issues-update-notes"
                                className="issues-textarea"
                                placeholder="Add a note"
                                value={updateNotes}
                                onChange={(e) => setUpdateNotes(e.target.value)}
                            />
                        </div>
                        <div className="issues-modal-footer">
                            <button className="btn-modal-cancel" onClick={() => setDiscrepancyToUpdate(null)}>
                                Cancel
                            </button>
                            <button className="btn-modal-primary" onClick={() => updateDiscrepancyStatus()}>
                                Update
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Modal: Classify Issue Type ─── */}
            {!!equipmentToUpdate && (
                <div
                    className="issues-modal-overlay"
                    onClick={() => {
                        setEquipmentToUpdate(null);
                        setDiscrepancyToUpdateType(null);
                        setArchivedDiscrepancyToUpdateType(null);
                    }}
                >
                    <div className="issues-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="issues-modal-header">
                            <h2>
                                Assign Issue Type to{' '}
                                {discrepancyToUpdateType?.equipmentName ||
                                    archivedDiscrepancyToUpdateType?.equipmentName}
                            </h2>
                        </div>
                        <div className="issues-modal-body">
                            <div className="issues-radio-group">
                                {getDownReasonOptions().map((opt) => (
                                    <div className="issues-radio-option" key={opt.value}>
                                        <input
                                            type="radio"
                                            id={`reason-${opt.value}`}
                                            name="downReason"
                                            value={opt.value}
                                            checked={(updateStatus || '') === opt.value}
                                            onChange={(e) =>
                                                setUpdateStatus(e.target.value.length ? e.target.value : null)
                                            }
                                        />
                                        <label htmlFor={`reason-${opt.value}`}>{opt.label}</label>
                                    </div>
                                ))}
                            </div>
                            <p className="warning-text">* This selection cannot be edited once confirmed *</p>
                        </div>
                        <div className="issues-modal-footer">
                            <button
                                className="btn-modal-cancel"
                                onClick={() => {
                                    setEquipmentToUpdate(null);
                                    setDiscrepancyToUpdateType(null);
                                    setArchivedDiscrepancyToUpdateType(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button className="btn-modal-primary" onClick={() => updateEquipmentAndDiscrepancy()}>
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Modal: Email Notifications ─── */}
            {shouldShowUpdateNotificationsModal && (
                <div
                    className="issues-modal-overlay"
                    onClick={() => {
                        setSearchParams(SEARCH_PARAMS.EMPTY);
                        setShouldShowUpdateNotificationsModal(false);
                    }}
                >
                    <div className="issues-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="issues-modal-header">
                            <h2>Receive Issues Email Notifications</h2>
                            <p>Notifications are sent when reports containing issues are submitted.</p>
                        </div>
                        <div className="issues-modal-body">
                            <div className="issues-checkbox">
                                <input
                                    type="checkbox"
                                    id="notif-inop"
                                    checked={JSON.parse(newDiscrepancyNotificationTypes)?.inop || false}
                                    onChange={(e) => {
                                        const existing = JSON.parse(newDiscrepancyNotificationTypes || '{}');
                                        setNewDiscrepancyNotificationTypes(
                                            JSON.stringify({ ...existing, inop: e.target.checked }),
                                        );
                                    }}
                                />
                                <label htmlFor="notif-inop">Out Of Service Notifications</label>
                            </div>
                            <div className="issues-checkbox">
                                <input
                                    type="checkbox"
                                    id="notif-warn"
                                    checked={JSON.parse(newDiscrepancyNotificationTypes)?.warn || false}
                                    onChange={(e) => {
                                        const existing = JSON.parse(newDiscrepancyNotificationTypes || '{}');
                                        setNewDiscrepancyNotificationTypes(
                                            JSON.stringify({ ...existing, warn: e.target.checked }),
                                        );
                                    }}
                                />
                                <label htmlFor="notif-warn">Warning Notifications</label>
                            </div>
                        </div>
                        <div className="issues-modal-footer">
                            <button
                                className="btn-modal-cancel"
                                onClick={() => {
                                    setSearchParams(SEARCH_PARAMS.EMPTY);
                                    setShouldShowUpdateNotificationsModal(false);
                                }}
                            >
                                Close
                            </button>
                            <button
                                className="btn-modal-primary"
                                onClick={() => {
                                    updateEmailNotifications();
                                    setSearchParams(SEARCH_PARAMS.EMPTY);
                                    setShouldShowUpdateNotificationsModal(false);
                                }}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
